import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { Check, Dumbbell, Plus } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { BotonEnlace, Button } from '@/components/Button';
import { EstadoVacio, Seccion } from '@/components/Card';
import { Segmentado } from '@/components/Segmentado';
import {
  agregarEjercicioAEntrenamiento,
  leerEnCurso,
  leerOpcionesParaAgregar,
  type EleccionAlAgregar,
  type OpcionesParaAgregar,
} from '@/db/entrenamientos';
import { mensajeDeError } from '@/db/tx';
import type { RoutineExercise } from '@/db/types';
import { admitePorLado, biserieAlVolver, textoDescanso, textoObjetivo } from '@/domain/entrenamiento';

/** "Prensa", "Prensa y Curl", "Prensa, Curl y Abductora". */
function nombresConY(nombres: readonly string[]): string {
  if (nombres.length <= 1) return nombres.join('');
  return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
}

/**
 * La biserie con que entraría hoy (0.3.9): con quién, si su pareja está en
 * este entrenamiento; si no, que va solo y por qué. Antes decía "biserie A"
 * aunque la pareja ya no estuviera.
 */
function textoDeBiserie(item: RoutineExercise, opciones: OpcionesParaAgregar): string | null {
  const clave = item.biserie ?? null;
  if (clave === null) return null;
  const { enElEntrenamiento, ejercicio } = opciones;
  if (biserieAlVolver(enElEntrenamiento, ejercicio.id, clave) !== null) {
    return `en biserie con ${nombresConY(enElEntrenamiento.filter((x) => x.biserie === clave).map((x) => x.nombre))}`;
  }
  return enElEntrenamiento.some((x) => x.biserie === clave)
    ? 'va solo: ya está en su biserie de hoy'
    : 'va solo: su pareja de biserie no está en este entrenamiento';
}

/** "3 × 10–15 por lado · descanso 1 min 30 s · pausa 2 s · en biserie con Puente" */
function textoDeConfiguracion(item: RoutineExercise, opciones: OpcionesParaAgregar): string {
  return [
    textoObjetivo(item.series, item.repsMin, item.repsMax, item.porLado === true),
    `descanso ${textoDescanso(item.descansoSeg)}`,
    item.pausaSeg != null && item.pausaSeg > 0 ? `pausa ${item.pausaSeg} s` : null,
    textoDeBiserie(item, opciones),
  ]
    .filter(Boolean)
    .join(' · ');
}

const iguales = (a: EleccionAlAgregar | null, b: EleccionAlAgregar) =>
  a !== null && a.tipo === b.tipo && (a.tipo === 'rutina' && b.tipo === 'rutina' ? a.routineExerciseId === b.routineExerciseId : true);

/**
 * Antes de agregar un ejercicio a mitad del entrenamiento (0.3.8): cómo se
 * hace hoy. Si la rutina de este entrenamiento lo tiene, con su configuración
 * (objetivo, descanso, indicaciones, por lado); si lo tiene varias veces, se
 * elige cuál, sin escoger una por su cuenta. A mano, se dice si se anota por
 * lado (incidencia del 26 oct 2026: el desplante volvía sin sus lados).
 */
export function AgregarAEntrenamientoPage() {
  const { exerciseId = '' } = useParams();
  const enCurso = useLiveQuery(() => leerEnCurso(), []);
  const opciones = useLiveQuery(
    async () => (enCurso === undefined || enCurso === null ? null : await leerOpcionesParaAgregar(enCurso.id, exerciseId)),
    [enCurso?.id, exerciseId],
  );
  if (enCurso === undefined || opciones === undefined) return null;
  if (enCurso === null) {
    return (
      <div>
        <PageHeader titulo="Sin entrenamiento en curso." volver={{ a: '/gym', texto: 'Gym', reemplazar: true }} />
        <EstadoVacio icono={Dumbbell} titulo="No hay nada abierto." descripcion="Empieza un entrenamiento para agregarle ejercicios." />
      </div>
    );
  }
  if (opciones === null) {
    // El entrenamiento sigue abierto: lo que falta es el ejercicio (0.3.9; antes decía "Sin entrenamiento en curso")
    return (
      <div>
        <PageHeader
          titulo="Ese ejercicio ya no está."
          volver={{ a: '/gym/entrenamiento', texto: 'Entrenamiento', etiqueta: 'Volver al entrenamiento', reemplazar: true }}
        />
        <EstadoVacio
          icono={Dumbbell}
          titulo="No se encontró en tus ejercicios."
          descripcion="Tu entrenamiento sigue abierto, sin cambios. Vuelve a él y elige otro ejercicio."
        >
          <BotonEnlace to="/gym/entrenamiento" variante="secundario" reemplazar>
            Volver al entrenamiento
          </BotonEnlace>
        </EstadoVacio>
      </div>
    );
  }
  return <FormularioAgregar key={`${enCurso.id}-${exerciseId}`} workoutId={enCurso.id} opciones={opciones} />;
}

function FormularioAgregar({ workoutId, opciones }: { workoutId: string; opciones: OpcionesParaAgregar }) {
  const navegar = useNavigate();
  const { ejercicio, deLaRutina, porLadoLaVezPasada, yaEsta } = opciones;
  const [eleccion, setEleccion] = useState<EleccionAlAgregar | null>(opciones.propuesta);
  // A mano, por lado o no: como la vez pasada, y se puede cambiar
  const [porLado, setPorLado] = useState(porLadoLaVezPasada);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const puedePorLado = admitePorLado(ejercicio.tipo);
  const manual = eleccion?.tipo === 'manual';

  async function agregar() {
    if (guardando || eleccion === null) return;
    setGuardando(true);
    setError(null);
    try {
      await agregarEjercicioAEntrenamiento(workoutId, ejercicio.id, eleccion.tipo === 'manual' ? { tipo: 'manual', porLado } : eleccion);
      void navegar('/gym/entrenamiento', { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  const opcion = (clave: string, valor: EleccionAlAgregar, titulo: string, detalle: string | null) => {
    const activa = iguales(eleccion, valor);
    return (
      <button
        key={clave}
        type="button"
        aria-pressed={activa}
        onClick={() => setEleccion(valor)}
        className={[
          'grid min-h-14 w-full gap-0.5 rounded-2xl border px-3 py-2.5 text-left',
          activa ? 'border-marca bg-marca-suave' : 'border-borde bg-superficie',
        ].join(' ')}
      >
        <span className="flex items-center gap-1.5 text-[14px] font-medium text-texto">
          {activa && <Check className="size-4 text-marca" aria-hidden="true" />}
          {titulo}
        </span>
        {/* Sobre el acento suave, el texto secundario no llega a 4.5:1: va en el principal */}
        {detalle !== null && <span className={`text-[12px] leading-relaxed ${activa ? 'text-texto' : 'text-texto-suave'}`}>{detalle}</span>}
      </button>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        antetitulo="Agregar al entrenamiento"
        titulo={ejercicio.nombre}
        descripcion={
          deLaRutina.length > 0
            ? 'Está en la rutina de este entrenamiento: se agrega como lo tienes ahí.'
            : 'No está en la rutina de este entrenamiento.'
        }
        volver={{ a: '/gym/entrenamiento', texto: 'Entrenamiento', etiqueta: 'Volver al entrenamiento sin agregarlo', reemplazar: true }}
      />

      {yaEsta > 0 && (
        <p className="text-[12px] leading-relaxed text-texto-suave">
          Ya está en este entrenamiento{yaEsta > 1 ? ` (${yaEsta} veces)` : ''}: se agrega otra vez, con sus propias series.
        </p>
      )}

      <Seccion
        titulo="Cómo lo haces hoy"
        descripcion={
          deLaRutina.length > 1
            ? 'Tu rutina lo tiene más de una vez, con distinta configuración. Elige cuál.'
            : deLaRutina.length === 1
              ? 'Con el objetivo, el descanso y las indicaciones de tu rutina.'
              : undefined
        }
      >
        <div role="group" aria-label="Configuración del ejercicio" className="grid gap-2">
          {deLaRutina.map((item, i) =>
            opcion(
              item.id,
              { tipo: 'rutina', routineExerciseId: item.id },
              deLaRutina.length > 1 ? `Como el ${i + 1}.º de tu rutina` : 'Como en tu rutina',
              [textoDeConfiguracion(item, opciones), item.notas].filter(Boolean).join('. '),
            ),
          )}
          {opcion(
            'manual',
            { tipo: 'manual', porLado },
            deLaRutina.length > 0 ? 'A mi manera, sin la configuración de la rutina' : 'A mi manera',
            'Sin objetivo de repeticiones, con tu descanso predeterminado.',
          )}
        </div>
      </Seccion>

      {manual && puedePorLado && (
        <div className="space-y-2">
          <Segmentado
            etiqueta="Cómo lo anotas"
            etiquetaVisible
            completo
            valor={porLado ? 'lado' : 'reps'}
            opciones={[
              { valor: 'reps', etiqueta: 'Repeticiones' },
              { valor: 'lado', etiqueta: 'Por lado' },
            ]}
            onCambio={(v) => setPorLado(v === 'lado')}
          />
          <p className="text-[12px] leading-relaxed text-texto-suave">
            Por lado: anotas las de la izquierda y las de la derecha, y los dos lados cuentan como una serie.
            {porLadoLaVezPasada ? ' La vez pasada lo anotaste por lado.' : ''}
          </p>
        </div>
      )}

      {error !== null && (
        <Banner tono="alerta" titulo="No se agregó">
          {error}
        </Banner>
      )}

      <Button icono={Plus} disabled={guardando || eleccion === null} onClick={() => void agregar()}>
        {guardando ? 'Agregando…' : 'Agregar al entrenamiento'}
      </Button>
      {eleccion === null && <p className="text-[12px] text-texto-suave">Elige una configuración para agregarlo.</p>}
      {/* Volver reemplaza esta pantalla: "atrás" desde el entrenamiento no regresa aquí (0.3.9) */}
      <BotonEnlace to="/gym/entrenamiento" variante="fantasma" reemplazar>
        Volver sin agregarlo
      </BotonEnlace>
    </div>
  );
}
